import React, { Component } from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import assignColors from 'utils/assignColors';
import styled from 'styled-components';
import RingChart from '../../../components/RingChart/RingChart';
import AssetLegend from '../../../components/AssetLegend';
import { translateText } from '../../../utils/helpers';

@connect((state) => ({
  french: state.Storage.language === 'fr',
  exchangeRate: state.Storage['pwpa-exchange-rate'],
  partnerName: state.Storage['partner-name'],
}))
class AssetChart extends Component {
  getChartData = (accounts, french) => {
    const { exchangeRate } = this.props;

    const filteredAccounts = accounts.filter((account) =>
      Object.values(account.asset_allocation).some((value) => value !== 0)
    );

    const hasCADAccounts = filteredAccounts.some(
      (account) => account.currency === 'CAD'
    );

    const getUnmatchedValue = (account) =>
      (account.holdings || [])
        .filter((holding) => !holding.security_detail)
        .reduce((sum, holding) => {
          let value = holding.market_value;
          if (account.currency === 'USD' && hasCADAccounts) {
            value *= exchangeRate;
          }
          return sum + value;
        }, 0);

    const totalHoldings = filteredAccounts.reduce((total, account) => {
      const unmatched = getUnmatchedValue(account, hasCADAccounts);
      const balance =
        account.currency === 'USD' && hasCADAccounts
          ? (account.balance - account.cash) * Number(exchangeRate) - unmatched
          : account.balance - account.cash - unmatched;

      return total + balance;
    }, 0);

    const categories = ['Equity', 'Fixed Income', 'Alternative', 'Misc'];
    const allHoldings = filteredAccounts.reduce((result, account) => {
      const { asset_allocation } = account;
      const unmatched = getUnmatchedValue(account);
      const accountTotalHoldings =
        account.currency === 'USD' && hasCADAccounts
          ? (account.balance - account.cash) * Number(exchangeRate) - unmatched
          : account.balance - account.cash - unmatched;
      const weight = accountTotalHoldings / Math.abs(totalHoldings);

      categories.forEach((category) => {
        const label = category === 'Misc' ? 'Misc.' : category;
        result[label] += asset_allocation[category] * weight;
      });

      return result;
    }, Object.fromEntries(categories.map((category) => [category === 'Misc' ? 'Misc.' : category, 0])));

    let hasNegative = false;
    const formattedHoldings = {};
    let totalAssetAllocation = 0;

    Object.entries(allHoldings).forEach(([category, value]) => {
      if (value < 0) {
        hasNegative = true;
      }
      if (value !== 0) {
        formattedHoldings[category] = value;
        totalAssetAllocation += value;
      }
    });

    const labels = Object.keys(formattedHoldings).map((label) =>
      translateText(label, french)
    );

    return {
      labels,
      values: Object.values(formattedHoldings),
      filtered: formattedHoldings,
      negative: hasNegative,
      datasets: [
        {
          data: Object.values(formattedHoldings),
          backgroundColor: assignColors(Object.keys(formattedHoldings)),
        },
      ],
      totalAssetAllocation,
    };
  };

  render() {
    const { accounts, french, partnerName } = this.props;
    const chartData = this.getChartData(accounts, french);

    // Chart options for barchart that shows when there is a negative value
    const chartOptions = {
      showAllTooltips: true,
      maintainAspectRatio: false,
      cutoutPercentage: 50,
      responsive: true,
      layout: {
        padding: {
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
        },
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title: (tooltipItems, data) => data.labels[tooltipItems[0].index],
          label: (tooltipItems, data) =>
            `${
              Math.round(
                data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ] * 10000
              ) / 100
            }%`,
        },
      },
      hover: {
        onHover: (e) => {
          e.target.style.cursor = 'pointer';
        },
      },
    };

    return (
      <div>
        {chartData.negative ? (
          <BarChartWrap>
            <HorizontalBar
              data={chartData}
              options={chartOptions}
              style={{
                margin: '0 4rem 0 0',
              }}
            />
          </BarChartWrap>
        ) : (
          <RingChartWrap>
            <AssetLegend
              data={chartData.filtered}
              french={french}
              partnerName={partnerName}
            />
            <RingChart
              labels={chartData.labels}
              values={chartData.values}
              colors={assignColors(chartData.labels)}
              maxHeight={350}
              maxWidth={350}
            />
          </RingChartWrap>
        )}
      </div>
    );
  }
}

AssetChart.propTypes = {
  accounts: PropTypes.array,
  french: PropTypes.bool,
  exchangeRate: PropTypes.number,
  partnerName: PropTypes.string,
};

AssetChart.defaultProps = {
  accounts: [],
  french: false,
  exchangeRate: 0,
  partnerName: '',
};

export default AssetChart;

const RingChartWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  column-gap: 1rem;
`;

const BarChartWrap = styled.div`
  height: 350px;
`;
